import React, { createContext, useContext, useEffect, useState } from 'react';

// Determine brand from subdomain (e.g., 'downtowners' from 'downtowners.deepmavenai.com')
function detectBrand() {
  const host = window.location.hostname;
  const parts = host.split('.');
  // If there is a subdomain (more than two parts), use it; otherwise fallback to 'default'
  const sub = parts.length > 2 ? parts[0] : parts[0];
  return sub || 'default';
}

const BrandingContext = createContext(null);

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    const brand = detectBrand();
    const defaultManifest = '/assets/branding/default/branding.json';
    const manifestPath = `/assets/branding/${brand}/branding.json`;
    // Try fetching the brand-specific manifest, fallback to default on error
    fetch(manifestPath)
      .then((res) => {
        if (!res.ok) throw new Error('Manifest not found');
        return res.json();
      })
      .catch(() => fetch(defaultManifest).then((res) => res.json()))
      .then((json) => setBranding(json))
      .catch((err) => console.error('Failed to load branding manifest:', err));
  }, []);

  // Inject CSS custom properties for colors and set global font
  useEffect(() => {
    if (!branding) return;
    const root = document.documentElement;
    if (branding.primaryColor) root.style.setProperty('--primary-color', branding.primaryColor);
    if (branding.secondaryColor) root.style.setProperty('--secondary-color', branding.secondaryColor);
    if (branding.fontFamily) {
      root.style.setProperty('--font-family', branding.fontFamily);
      document.body.style.fontFamily = branding.fontFamily;
    }
  }, [branding]);
  
  // Inject custom CSS for branding (e.g., /assets/branding/{brand}/custom.css)
  useEffect(() => {
    if (!branding || !branding.customCss) return;
    let link = document.getElementById('branding-custom-css');
    if (!link) {
      link = document.createElement('link');
      link.id = 'branding-custom-css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = branding.customCss;
  }, [branding]);

  // Inject hero gradient & glow colors from branding manifest
  useEffect(() => {
    if (!branding) return;
    const { heroGradient, glowColors } = branding;
    let style = document.getElementById('branding-overrides');
    if (!style) {
      style = document.createElement('style');
      style.id = 'branding-overrides';
      document.head.appendChild(style);
    }
    const lines = [];
    if (heroGradient) {
      lines.push(`:root { --hero-gradient: ${heroGradient}; }`);
    }
    if (Array.isArray(glowColors) && glowColors.length === 5) {
      lines.push(`.glowing-border.hero {`);
      glowColors.forEach((color, idx) => {
        lines.push(`  --clr-${idx + 1}: ${color};`);
      });
      lines.push(`}`);
    }
    style.innerHTML = lines.join('\n');
  }, [branding]);

  // Update document title and favicon
  useEffect(() => {
    if (!branding) return;
    // Set page title
    document.title = branding.siteTitle || branding.appName || branding.brandName;

    // Update favicon
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    if (branding.favicon) link.href = branding.favicon;
  }, [branding]);

  // While loading, optionally render nothing or a loader
  if (!branding) return null;

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

// Hook to access branding data
export function useBranding() {
  return useContext(BrandingContext);
}